// ThemeDetailRow.tsx
import React from 'react';

type ThemeDetailRowProps = {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  className?: string;
  labelWidth?: 'auto' | 'sm' | 'md' | 'lg' | 'xl' | number; // Fixed width options
  orientation?: 'horizontal' | 'vertical'; // Stack on mobile
  responsiveBreakpoint?: 'sm' | 'md' | 'lg' | 'xl'; // When to switch to horizontal
  align?: 'start' | 'center' | 'end'; // Vertical alignment
  labelClassName?: string;
  valueClassName?: string;
  iconClassName?: string;
  truncate?: boolean; // Truncate long values
  copyable?: boolean; // Add copy to clipboard functionality
};

const labelWidthClasses = {
  auto: '',
  sm: 'w-20',
  md: 'w-24',
  lg: 'w-32',
  xl: 'w-40'
};

export const ThemeDetailRow: React.FC<ThemeDetailRowProps> = ({ 
  icon, 
  label, 
  value,
  className = '',
  labelWidth = 'md',
  orientation = 'horizontal',
  responsiveBreakpoint = 'md',
  align = 'start',
  labelClassName = '',
  valueClassName = '',
  iconClassName = '',
  truncate = false,
  copyable = false
}) => {
  const [copied, setCopied] = React.useState(false);
  
  const widthClass = typeof labelWidth === 'number' 
    ? '' 
    : labelWidthClasses[labelWidth];
  
  const customWidth = typeof labelWidth === 'number' 
    ? { width: `${labelWidth}px`, flexShrink: 0 }
    : {};
  
  const alignClass = align === 'start' ? 'items-start' : 
                     align === 'center' ? 'items-center' : 'items-end';
  
  const truncateClass = truncate ? 'truncate' : '';
  
  const handleCopy = async () => {
    if (typeof value === 'string') {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  // Horizontal layout (default)
  const HorizontalLayout = () => (
    <div className={`flex ${alignClass} gap-3 text-sm ${className}`}>
      {icon && (
        <div className={`flex-shrink-0 w-5 text-gray-400 ${iconClassName}`}>
          {icon}
        </div>
      )}
      <div 
        className={`flex-shrink-0 text-gray-500 dark:text-gray-400 ${widthClass} ${labelClassName}`}
        style={customWidth}
      >
        {label}:
      </div>
      <div className={`flex-1 text-gray-900 dark:text-gray-100 font-medium ${truncateClass} ${valueClassName}`}>
        {copyable && typeof value === 'string' ? (
          <div className="flex items-center gap-2 group">
            <span className={truncateClass}>{value}</span>
            <button
              onClick={handleCopy}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Copy to clipboard"
            >
              {copied ? (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              )}
            </button>
          </div>
        ) : (
          value
        )}
      </div>
    </div>
  );
  
  // Vertical layout (stacked)
  const VerticalLayout = () => (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className={`flex items-center ${align === 'start' ? 'items-start' : 'items-center'} gap-2`}>
        {icon && (
          <div className={`flex-shrink-0 w-4 text-gray-400 ${iconClassName}`}>
            {icon}
          </div>
        )}
        <span className={`text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide ${labelClassName}`}>
          {label}
        </span>
      </div>
      <div className={`text-gray-900 dark:text-gray-100 font-medium ${truncateClass} ${valueClassName}`}>
        {copyable && typeof value === 'string' ? (
          <div className="flex items-center gap-2 group">
            <span className={truncateClass}>{value}</span>
            <button
              onClick={handleCopy}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Copy to clipboard"
            >
              {copied ? (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              )}
            </button>
          </div>
        ) : (
          value
        )}
      </div>
    </div>
  );
  
  // Responsive layout - switch between horizontal and vertical
  if (orientation === 'vertical') {
    return <VerticalLayout />;
  }
  
  // For responsive: horizontal on desktop, vertical on mobile
  const responsiveClasses = {
    sm: 'flex-col sm:flex-row sm:items-center gap-1 sm:gap-3',
    md: 'flex-col md:flex-row md:items-center gap-1 md:gap-3',
    lg: 'flex-col lg:flex-row lg:items-center gap-1 lg:gap-3',
    xl: 'flex-col xl:flex-row xl:items-center gap-1 xl:gap-3'
  };
  
  const responsiveClass = responsiveClasses[responsiveBreakpoint];
  
  const ResponsiveLayout = () => (
    <div className={`flex ${responsiveClass} ${align === 'start' ? 'items-start' : align === 'center' ? 'items-center' : 'items-end'} ${className}`}>
      <div className="flex items-center gap-2 sm:gap-3">
        {icon && (
          <div className={`flex-shrink-0 w-4 sm:w-5 text-gray-400 ${iconClassName}`}>
            {icon}
          </div>
        )}
        <span className={`text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 ${labelClassName}`}>
          {label}:
        </span>
      </div>
      <div className={`flex-1 text-sm sm:text-base text-gray-900 dark:text-gray-100 font-medium ${truncateClass} ${valueClassName} ml-6 sm:ml-0`}>
        {copyable && typeof value === 'string' ? (
          <div className="flex items-center gap-2 group">
            <span className={truncateClass}>{value}</span>
            <button
              onClick={handleCopy}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Copy to clipboard"
            >
              {copied ? (
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              )}
            </button>
          </div>
        ) : (
          value
        )}
      </div>
    </div>
  );
  
  // Choose layout based on orientation
  if (orientation === 'horizontal') {
    return <HorizontalLayout />;
  }
  
  if (orientation === 'vertical') {
    return <VerticalLayout />;
  }
  
  return <ResponsiveLayout />;
};

// Optional: Create a grid of detail rows for better alignment
type ThemeDetailGridProps = {
  rows: Array<Omit<ThemeDetailRowProps, 'className'>>;
  columns?: 1 | 2 | 3 | 4;
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
};

export const ThemeDetailGrid: React.FC<ThemeDetailGridProps> = ({ 
  rows, 
  columns = 2, 
  gap = 'md',
  className = ''
}) => {
  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8'
  };
  
  const columnClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };
  
  return (
    <div className={`grid ${columnClasses[columns]} ${gapClasses[gap]} ${className}`}>
      {rows.map((row, index) => (
        <ThemeDetailRow key={index} {...row} />
      ))}
    </div>
  );
};

// Optional: Group of detail rows with consistent spacing
type ThemeDetailGroupProps = {
  children: React.ReactNode;
  spacing?: 'sm' | 'md' | 'lg';
  className?: string;
};

export const ThemeDetailGroup: React.FC<ThemeDetailGroupProps> = ({ 
  children, 
  spacing = 'md',
  className = ''
}) => {
  const spacingClasses = {
    sm: 'space-y-2',
    md: 'space-y-3',
    lg: 'space-y-4'
  };
  
  return (
    <div className={`${spacingClasses[spacing]} ${className}`}>
      {children}
    </div>
  );
};